/**
 * The one place a profile's location patch is assembled.
 *
 * A location is stored SEVEN times over: the three human-readable names
 * (`country`, `region`, `city`), the two ISO codes (`countryCode`,
 * `regionCode`) and the coordinate pair. Nothing on the server derives one from
 * another, so any surface that writes a name without its code — or keeps
 * yesterday's coordinates next to today's city — silently leaves the profile
 * describing two different places. `locationPatch` is the only sanctioned way to
 * build that write: every field it returns comes from the SAME selection, and a
 * level that is not selected is written as `null` rather than left behind.
 *
 * Three rules it enforces so callers cannot forget them:
 *
 * 1. Cascade. A region without a country, or a city without a region, is
 *    dropped — the codes are only meaningful relative to their parent.
 * 2. Pairing. A name and its code always come off the same place object, so a
 *    code can never survive a rename.
 * 3. One anchor. The coordinates come from the most specific place IN the patch
 *    (city, else region, else country) and are `null` when that place has none —
 *    never inherited from a shallower one, so an unmapped city stays
 *    backfillable instead of being pinned to its region's centroid.
 *
 * The coordinate bounds are `profile-fields`' own `LATITUDE_SCHEMA` /
 * `LONGITUDE_SCHEMA`, so the browser can never offer a pair the write refuses.
 */

import { LATITUDE_SCHEMA, LOCATION_MAX_LENGTH, LONGITUDE_SCHEMA } from './profile-fields';

/** A row as the `countries` / `states` / `cities` queries return it. */
export type LocationPlace = {
  name: string;
  isoCode?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
};

export type LocationSelection = {
  country?: LocationPlace | null;
  region?: LocationPlace | null;
  city?: LocationPlace | null;
};

/** Exactly the `UpdateProfileInput` location fields, all of them, every time. */
export type LocationPatch = {
  country: string | null;
  countryCode: string | null;
  region: string | null;
  regionCode: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
};

/** What a profile row holds today — the shape the editor seeds itself from. */
export type StoredLocation = {
  country?: string | null;
  countryCode?: string | null;
  region?: string | null;
  regionCode?: string | null;
  city?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
};

export const EMPTY_LOCATION: LocationPatch = {
  country: null,
  countryCode: null,
  region: null,
  regionCode: null,
  city: null,
  latitude: null,
  longitude: null,
};

type Resolved = {
  name: string;
  code: string | null;
  latitude: number | null;
  longitude: number | null;
};

function coordinate(value: string | number | null | undefined, schema: typeof LATITUDE_SCHEMA): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  const result = schema.safeParse(parsed);
  return result.success && typeof result.data === 'number' ? result.data : null;
}

function resolve(place: LocationPlace | null | undefined): Resolved | null {
  const name = (place?.name ?? '').trim();
  if (!name) {
    return null;
  }
  const code = (place?.isoCode ?? '').trim();
  return {
    name,
    code: code || null,
    latitude: coordinate(place?.latitude, LATITUDE_SCHEMA),
    longitude: coordinate(place?.longitude, LONGITUDE_SCHEMA),
  };
}

/**
 * The anchor is the DEEPEST place in the patch and nothing shallower. When that
 * place has no coordinates the pair is written as `null` rather than borrowed
 * from its parent — a free-typed city (Belgium's provinces carry no city list,
 * so the picker falls back to free text) must not be pinned to its province's
 * centroid. `api/src/jobs/user/geolocate-profile.ts` only backfills a profile
 * whose latitude AND longitude are both null, so inheriting a coarse parent
 * would cap that profile at region-level precision permanently, where leaving
 * the pair null gets it real coordinates from the user's IP moments later.
 *
 * Both halves or neither: half a pair is not a location.
 */
function anchor(...places: Array<Resolved | null>): Resolved | null {
  const deepest = places.find((place) => place) ?? null;
  if (!deepest || deepest.latitude === null || deepest.longitude === null) {
    return null;
  }

  return deepest;
}

/**
 * Builds the complete location write from one selection. Clearing the country
 * clears everything, which is how a profile drops its location entirely.
 */
export function locationPatch(selection: LocationSelection): LocationPatch {
  const country = resolve(selection.country);
  if (!country) {
    return { ...EMPTY_LOCATION };
  }

  const region = resolve(selection.region);
  const city = region ? resolve(selection.city) : null;
  const anchored = anchor(city, region, country);

  return {
    country: country.name,
    countryCode: country.code,
    region: region?.name ?? null,
    regionCode: region?.code ?? null,
    city: city?.name ?? null,
    latitude: anchored?.latitude ?? null,
    longitude: anchored?.longitude ?? null,
  };
}

/**
 * Seeds an editor from what is stored, applying the same cascade so a row that
 * has already drifted (a region with no country) opens as a consistent
 * selection rather than re-saving the drift.
 *
 * The stored coordinates ride on the most specific stored place, which is where
 * they came from — so opening the editor and saving without touching anything
 * writes the same pair back instead of erasing it.
 */
export function locationSelection(stored: StoredLocation | null | undefined): LocationSelection {
  const country = (stored?.country ?? '').trim();
  if (!country) {
    return {};
  }

  const region = (stored?.region ?? '').trim();
  const city = region ? (stored?.city ?? '').trim() : '';
  const coordinates = { latitude: stored?.latitude ?? null, longitude: stored?.longitude ?? null };

  return {
    country: { name: country, isoCode: stored?.countryCode ?? null, ...(region ? {} : coordinates) },
    ...(region ? { region: { name: region, isoCode: stored?.regionCode ?? null, ...(city ? {} : coordinates) } } : {}),
    ...(city ? { city: { name: city, ...coordinates } } : {}),
  };
}

/**
 * The city an owner typed, offered as its own option when the bundled list does
 * not carry it — or `null` when there is nothing to offer.
 *
 * The city dataset is not exhaustive: New York's list has 1,051 places and no
 * Millerton, so a member who lives there searched, read "No match.", and was
 * left with Poughkeepsie. Typing used to be possible only in a region with NO
 * list at all. Any region can now take a typed city, exactly as that fallback
 * stores one: a name and no coordinates (see the anchor rule above).
 *
 * Offered only when no listed place already has that name, so a typed "albany"
 * never shadows the real Albany and the coordinates that come with it.
 */
export function typedPlaceName(
  query: string | null | undefined,
  options: ReadonlyArray<{ name: string }>
): string | null {
  const name = (query ?? '').trim().replace(/\s+/g, ' ').slice(0, LOCATION_MAX_LENGTH);
  if (!name) return null;
  const folded = name.toLocaleLowerCase();
  if (options.some((option) => option.name.trim().toLocaleLowerCase() === folded)) return null;
  return name;
}

const LOCATION_FIELDS = Object.keys(EMPTY_LOCATION);

function blankToNull(value: unknown): unknown {
  if (value === '' || value === undefined) {
    return null;
  }
  return value;
}

/**
 * A save input whose location goes out whole — all seven fields, blanks as
 * explicit null — when any field differs from `baseline` (the values the form
 * was seeded with), and not at all otherwise. Whole, so a partial edit cannot
 * leave a torn row (a country's coordinates with no country name). Apollo strips
 * `undefined` keys, so a cleared location otherwise never reaches the server
 * (issue #4776); sending an untouched location would make every save a location
 * write.
 */
export function locationPayload(
  fields: Record<string, unknown>,
  baseline: Record<string, unknown>
): Record<string, unknown> {
  const location = LOCATION_FIELDS.reduce<Record<string, unknown>>((memo, field) => {
    memo[field] = blankToNull(fields[field]);
    return memo;
  }, {});
  const changed = LOCATION_FIELDS.some((field) => location[field] !== blankToNull(baseline[field]));
  const rest = Object.fromEntries(Object.entries(fields).filter(([field]) => !LOCATION_FIELDS.includes(field)));
  if (!changed) {
    return rest;
  }
  return { ...rest, ...location };
}
